import _ from 'lodash'
import { money } from '~/util/string'
import { deductibleTiersText } from '~/util/deductible-tiers'
// frontend/util/aep-recommendation.js
// The AEP recommendation engine (issue #721). Pure functions over the
// recommendation config and engine-shaped plans: no Vue, no store, no I/O.
// Spec: docs/superpowers/specs/2026-09-26-aep-recommendation-builder-721-design.md
// Client wording: Appendix A of the spec, transcribed from Dien's demo v4 —
// change it there first.

export const OFFICE_PHONE = '401-404-7373'
export const OFFICE_PHONE_HREF = 'tel:+14014047373'
export const HELP_EMAIL = 'help@doctorschoiceusa.com'
export const MEDICARE_PHONE = '1-800-633-4227'
export const MEDICARE_URL = 'https://www.medicare.gov'
export const CARE_COMPARE_URL = 'https://www.medicare.gov/care-compare'

// The coverage shapes the builder models (spec D6: SUPP / NONE / UNSURE are
// mapped onto one of these by the advisor).
export const COVERAGES = ['SUPP_PDP', 'PDP', 'MAPD']

export const DEFAULT_RECOMMENDATION = Object.freeze({
    coverage: null,
    current: { supp_id: null, pdp_id: null, mapd_id: null, verified: false },
    medical: null,
    drug: null,
    supp_id: null,
    pdp_id: null,
    mapd_id: null,
    reasons: null,
    note: '',
    current_supp_premium: null,
    household_discount: false,
    underwriting: null,
    // Round-2 spec §13: Option 2… for the client, same shape as the primary
    // (acts, ids, household_discount, underwriting); everything else is shared.
    alternatives: [],
})

// Spec §3.1. `isNew` marks the scenarios the demo badges "new".
export const PRESETS_BY_COVERAGE = {
    PDP: [
        { label: 'Staying PDP', medical: 'keep', drug: 'keep' },
        { label: 'Switching PDP', medical: 'keep', drug: 'switch' },
    ],
    MAPD: [
        { label: 'Staying MAPD', medical: 'keep', drug: 'keep' },
        { label: 'Switching MAPD', medical: 'switch', drug: 'keep' },
        { label: 'MAPD to Supp + PDP', medical: 'to_supp', drug: 'keep', isNew: true },
    ],
    SUPP_PDP: [
        { label: 'Staying Supp + PDP', medical: 'keep', drug: 'keep' },
        { label: 'Switching PDP', medical: 'keep', drug: 'switch' },
        { label: 'Switching Supp', medical: 'switch', drug: 'keep', isNew: true },
        { label: 'Switching Supp + PDP', medical: 'switch', drug: 'switch', isNew: true },
        { label: 'Supp + PDP to MAPD', medical: 'to_mapd', drug: 'keep', isNew: true },
    ],
}

// Carrier-specific self-enroll instructions that exist as components today
// (mixins/card.js isPlanBcbsmaMapd / isPlanBlueRxPdpOutsideRI / isPlanHumanaPdp).
// A match forces method 'self' (spec §6.1). The next-steps card no longer
// shows them: self-enroll goes through Medicare.gov (Dien 2026-10-02), the
// ICHRA Humana exception included (2026-10-06, it carried Benefit Bay's ARC
// payment steps and Benefit Bay no longer administers these ICHRAs).
export const CARRIER_SELF_COMPONENTS = {
    bcbsma_mapd: 'CardStepCarrierBcbsmaMapdEnroll',
    blue_rx_pdp: 'CardStepCarrierBlueRxPdpEnroll',
    humana_pdp: 'CardStepCarrierHumanaPdpEnroll',
}

const SUPP_TYPE_LETTERS = { MA_CORE: 'CORE', MA_SUPP1: '1', MA_SUPP_1: '1', MA_SUPP2: '1A', MA_SUPP_1A: '1A', MA_SUPP_2: '1A' }
const LETTER_RE = /\b(?:plan)\s+([a-n]|hd[fg])\b/i

const HD_RE = /high[\s-]*deductible|\bHD\b/i

export function letterFromText (text) {
    const t = text || ''
    const m = LETTER_RE.exec(t)
    if (!m) { return null }
    const letter = m[1].toUpperCase()
    // "High Deductible Plan G" is its own plan (HDG), not Plan G (review I3).
    if ((letter === 'G' || letter === 'F') && HD_RE.test(t)) { return `HD${letter}` }
    return letter
}

// The Medigap letter of a plan: an explicit `letter`, else oracle's supp_type
// ('G', 'N', 'HDG', 'MA_CORE'...), else the name.
export function planLetter (plan) {
    if (!plan) { return null }
    if (plan.letter) { return String(plan.letter).toUpperCase() }
    const st = plan.supp_type ? String(plan.supp_type).toUpperCase() : null
    if (st) {
        if (SUPP_TYPE_LETTERS[st]) { return SUPP_TYPE_LETTERS[st] }
        if (/^([A-N]|HD[FG])$/.test(st)) { return st }
    }
    return letterFromText(plan.name)
}

export function carrierSelfComponent (plan, state) {
    if (!plan) { return null }
    const name = plan.name || ''
    const carrier = plan.carrier || ''
    const isPdp = !!plan.pdp || plan.type === 'pdp' || plan.plan_type === 'pdp'
    const isMapd = !!plan.mapd || !!plan.mapd_or_ma || plan.type === 'mapd' || plan.plan_type === 'mapd' || plan.plan_type === 'ma'
    if (isPdp && (/humana/i.test(name) || /humana/i.test(carrier))) { return CARRIER_SELF_COMPONENTS.humana_pdp }
    if (isPdp && (/Blue MedicareRx/i.test(name) || /Blue MedicareRx/i.test(carrier)) && (state || '').toUpperCase() !== 'RI') { return CARRIER_SELF_COMPONENTS.blue_rx_pdp }
    if (isMapd && /Blue Cross Blue Shield of Massachusetts/i.test(carrier)) { return CARRIER_SELF_COMPONENTS.bcbsma_mapd }
    return null
}

export function isInitial (coverage, rec) {
    if (!rec || !COVERAGES.includes(coverage)) { return true }
    if (coverage === 'PDP') { return rec.drug == null }
    if (coverage === 'MAPD') { return rec.medical == null }
    if (rec.medical === 'to_mapd') { return false }
    return rec.medical == null || rec.drug == null
}

const INITIAL = Object.freeze({ label: 'Initial', mode: null })

// Spec §3.2: scenario label + the legacy mode code written to config.mode.
export function scenario (coverage, rec) {
    if (isInitial(coverage, rec)) { return INITIAL }
    const m = rec.medical
    const d = rec.drug
    if (coverage === 'PDP') {
        return d === 'switch' ? { label: 'Switching PDP', mode: 'SWITCH_PDP' } : { label: 'Staying PDP', mode: 'STAY_PDP' }
    }
    if (coverage === 'MAPD') {
        if (m === 'keep') { return { label: 'Staying MAPD', mode: 'STAY_MAPD' } }
        if (m === 'switch') { return { label: 'Switching MAPD', mode: 'SWITCH_MAPD' } }
        return { label: 'MAPD to Supp + PDP', mode: 'MAPD_TO_SUPP_PDP' }
    }
    if (m === 'to_mapd') { return { label: 'Supp + PDP to MAPD', mode: 'SUPP_PDP_TO_MAPD' } }
    if (m === 'keep') {
        return d === 'keep' ? { label: 'Staying Supp + PDP', mode: 'STAY_SUPP_PDP' } : { label: 'Switching PDP, keeping Supp', mode: 'SWITCH_PDP' }
    }
    return d === 'keep' ? { label: 'Switching Supp, keeping PDP', mode: 'SWITCH_SUPP' } : { label: 'Switching Supp + PDP', mode: 'SWITCH_SUPP_PDP' }
}

export const toSupp = (coverage, rec) => coverage === 'MAPD' && rec?.medical === 'to_supp'
export const toMapd = (coverage, rec) => coverage === 'SUPP_PDP' && rec?.medical === 'to_mapd'
export const suppApp = (coverage, rec) => toSupp(coverage, rec) || (coverage === 'SUPP_PDP' && rec?.medical === 'switch')
export const isMove = lines => (lines || []).some(l => l.act === 'new')
// A kept supplement Flow cannot match -- a group supplement, or one the client
// does not want reviewed (2026-10-06, client 6319) -- is left out: the
// recommendation covers the drug plan only and never names the supplement.
export const suppLeftOut = (coverage, rec, current) => coverage === 'SUPP_PDP' && rec?.medical === 'keep' && !current?.supp
export const anyNew = lines => (lines || []).some(l => l.act !== 'keep')

// Spec §3.3. One entry per resulting coverage line, supp -> pdp, or mapd.
// A kept supplement with no current plan is left out (suppLeftOut).
// [] means "incomplete": Initial, a kept line with no current plan, a new plan
// not picked or not in the Plans list. A switch whose current plan is unmatched
// goes on with from: null and the prose degrades (Review Focus 1).
export function lines (coverage, rec, current, plansById) {
    if (isInitial(coverage, rec)) { return [] }
    const cur = current || {}
    const byId = plansById || {}
    const pick = id => (id == null ? null : byId[id] || null)
    const keep = p => (p ? { plan: p, act: 'keep', from: null } : null)
    const sw = (p, from) => (p ? { plan: p, act: 'switch', from: from || null } : null)
    const add = p => (p ? { plan: p, act: 'new', from: null } : null)
    let out
    if (coverage === 'PDP') {
        out = [rec.drug === 'keep' ? keep(cur.pdp) : sw(pick(rec.pdp_id), cur.pdp)]
    } else if (coverage === 'MAPD') {
        if (rec.medical === 'keep') { out = [keep(cur.mapd)] } else if (rec.medical === 'switch') { out = [sw(pick(rec.mapd_id), cur.mapd)] } else if (rec.medical === 'to_supp') { out = [add(pick(rec.supp_id)), add(pick(rec.pdp_id))] } else { return [] }
    } else if (rec.medical === 'to_mapd') {
        out = [add(pick(rec.mapd_id))]
    } else {
        const rx = rec.drug === 'keep' ? keep(cur.pdp) : sw(pick(rec.pdp_id), cur.pdp)
        out = suppLeftOut(coverage, rec, cur)
            ? [rx]
            : [rec.medical === 'keep' ? keep(cur.supp) : sw(pick(rec.supp_id), cur.supp), rx]
    }
    return out.every(Boolean) ? out : []
}

// The current plans a scenario replaces: on a move, the current plans of the
// coverage's lines (a stray id of another type is ignored); otherwise the
// `from` of each switch line (unmatched currents are simply absent).
const KINDS_BY_COVERAGE = { SUPP_PDP: ['supp', 'pdp'], PDP: ['pdp'], MAPD: ['mapd'] }
export function replacedPlans (coverage, rec, current, lns) {
    const cur = current || {}
    if (isMove(lns)) {
        return (KINDS_BY_COVERAGE[coverage] || []).map(k => cur[k]).filter(Boolean)
    }
    return (lns || []).filter(l => l.act === 'switch' && l.from).map(l => l.from)
}

// Spec §6.1.
export function method (plan, payer) {
    if (!plan) { return 'self' }
    if (plan.carrier_self) { return 'self' }
    if (!plan.enrollable) { return 'self' }
    if (!payer && !plan.commissionable) { return 'self' }
    return 'agent'
}

// ---- reasons (spec §10, Appendix A.1) --------------------------------------
export const REASONS = [
    { id: 'rx', label: 'Prescriptions', text: 'your updated prescriptions' },
    { id: 'total', label: 'Lower total yearly cost', text: 'a lower total yearly cost, including premiums and estimated drug costs' },
    { id: 'pharmacy', label: 'Preferred pharmacy', text: 'lower copays at your preferred pharmacy' },
    { id: 'robust', label: 'More robust medical coverage', text: 'more robust medical coverage' },
    { id: 'freedom', label: 'See any doctor', text: 'the freedom to see any doctor who accepts Medicare' },
    { id: 'travel', label: 'Travel / time out of state', text: 'coverage that goes with you when you travel or spend time out of state' },
    { id: 'doctors', label: 'Doctors in network', text: 'keeping your doctors in network' },
    { id: 'premium', label: 'Lower premium', text: 'a lower monthly premium' },
    { id: 'rate', label: 'Supp rate increase', text: 'the rate increase on your current supplement' },
    { id: 'moop', label: 'Lower max out-of-pocket', text: 'a lower out-of-pocket maximum' },
    { id: 'copays', label: 'Lower copays', text: 'lower copays for your medical care' },
    { id: 'dvh', label: 'Dental, vision and hearing', text: 'dental, vision and hearing benefits' },
    { id: 'costlier', label: 'Current plan getting more expensive', text: year => `your current plan becoming more expensive for ${year}` },
    { id: 'ending', label: 'Current plan ending', text: year => `your current plan not renewing for ${year}` },
    { id: 'works', label: 'Current plan still fits', text: 'your current coverage continuing to meet your needs' },
]
const REASON_ORDER = REASONS.map(r => r.id)
const PDP_SWITCH = ['rx', 'total', 'pharmacy', 'premium', 'costlier', 'ending']
const SUPP_SWITCH = ['rate', 'premium']
export const REASONS_BY_MODE = {
    STAY_PDP: ['rx', 'pharmacy', 'works'],
    SWITCH_PDP: PDP_SWITCH,
    STAY_MAPD: ['rx', 'doctors', 'dvh', 'works'],
    SWITCH_MAPD: ['rx', 'total', 'doctors', 'premium', 'moop', 'copays', 'dvh', 'costlier', 'ending'],
    MAPD_TO_SUPP_PDP: ['robust', 'freedom', 'travel', 'copays', 'rx', 'ending'],
    STAY_SUPP_PDP: ['freedom', 'rx', 'works'],
    SWITCH_SUPP: SUPP_SWITCH,
    SWITCH_SUPP_PDP: SUPP_SWITCH.concat(PDP_SWITCH.filter(x => x !== 'premium')),
    SUPP_PDP_TO_MAPD: ['premium', 'dvh', 'doctors', 'rx'],
}
export const DEFAULT_REASONS = {
    STAY_PDP: ['rx', 'works'],
    SWITCH_PDP: ['rx', 'total'],
    STAY_MAPD: ['rx', 'doctors', 'works'],
    SWITCH_MAPD: ['rx', 'doctors'],
    MAPD_TO_SUPP_PDP: ['robust', 'freedom'],
    STAY_SUPP_PDP: ['rx', 'works'],
    SWITCH_SUPP: ['rate'],
    SWITCH_SUPP_PDP: ['rate', 'rx'],
    SUPP_PDP_TO_MAPD: ['premium', 'dvh'],
}
const inReasonOrder = ids => REASON_ORDER.filter(id => ids.includes(id))

// A supp switch from a non-G letter to Plan G shows the coverage reasons too;
// a same-letter switch (or an unknown current letter) never does.
export function suppUpgrade (coverage, rec, current, plansById) {
    if (coverage !== 'SUPP_PDP' || rec?.medical !== 'switch') { return false }
    const curLetter = planLetter(current?.supp)
    const newLetter = planLetter((plansById || {})[rec.supp_id])
    return !!curLetter && curLetter !== 'G' && newLetter === 'G'
}
export function allowedReasons (mode, upgrade = false) {
    const base = (REASONS_BY_MODE[mode] || []).slice()
    if (upgrade) { base.push('robust', 'copays') }
    return inReasonOrder(_.uniq(base))
}
export const defaultReasons = mode => (DEFAULT_REASONS[mode] || []).slice()
export function effectiveReasons (rec, mode, upgrade = false) {
    const allowed = allowedReasons(mode, upgrade)
    const chosen = rec?.reasons == null ? defaultReasons(mode) : rec.reasons
    return inReasonOrder(chosen.filter(id => allowed.includes(id)))
}
export function defaultUnderwriting (coverage, rec, reasons) {
    return toSupp(coverage, rec) && (reasons || []).includes('ending') ? 'no' : 'yes'
}

// ---- cards (spec §8.1, §9) -------------------------------------------------
// Render order of the AEP catalog; the combo key sits at '<combo>'.
export const AEP_CARD_ORDER = [
    'cover', 'intro', 'next_steps_summary', 'part_a_and_b_overview', 'income_surcharges',
    '<combo>', 'current_plan_costs', 'current_vs_recommended', 'providers_network', 'coverage_gaps', 'supp_vs_ma', 'group_plans_compare',
    'step_intro', 'step_enroll_plan', 'step_actions_enroll_plan', 'step_actions_keep_plans',
    'step_part_a_and_b_signup', 'step_part_a_signup', 'step_part_b_signup', 'step_ssa44_file',
    'step_when_retiring', 'step_post_enrollment_watch', 'step_ichra',
    'recap', 'attachments', 'disclaimer',
]
// Switched like a card, rendered inside the plan card, never by the main loop (D8).
export const VIRTUAL_CARDS = ['providers_network']
// Cards whose text is regenerated from the recommendation; their hand-edited
// HTML is reset (after a confirm) when the recommendation changes (D5).
export const GENERATED_CARDS = ['step_enroll_plan', 'step_actions_enroll_plan', 'current_vs_recommended', 'supp_vs_ma', 'current_plan_costs']

export const comboKey = lns => (lns && lns.length ? `combo_${lns.map(l => l.plan.id).join('_')}` : null)

// ---- options (round-2 spec §13) ---------------------------------------------
// An alternative's effective recommendation: the primary's coverage, current
// plans and supp premium; its own acts, ids, household discount and
// underwriting; no reasons, no note, no nested options.
export const scenarioRec = (rec, alt) => ({ ...rec, ...(alt || {}), reasons: [], note: '', alternatives: [] })
// The primary first, then the complete, non-duplicate alternatives.
export const options = ctx => (ctx ? [ctx].concat(ctx.alternatives || []) : [])
const idsKey = plans => plans.map(p => p.id).slice().sort((a, b) => a - b).join('_')

export function agentPlans (ctx) {
    const excluded = ctx.excludedIds || []
    const ok = p => p && ctx.methodOf(p) === 'agent' && !excluded.includes(p.id)
    if (ctx.toSupp) {
        const supp = ctx.lines.map(l => l.plan).find(p => p.type === 'supp')
        return ok(supp) ? [supp] : []
    }
    return ctx.lines.filter(l => l.act !== 'keep').map(l => l.plan).filter(ok)
}
export const selfRxPlans = ctx => ctx.lines.filter(l => l.act !== 'keep' && l.plan.type !== 'supp' && ctx.methodOf(l.plan) === 'self').map(l => l.plan)

// Spec §9.1. Empty for an incomplete scenario (no recompute, spec §12).
export function autoCards (ctx) {
    if (!ctx || !ctx.lines.length) { return {} }
    const all = options(ctx)
    const on = {}
    AEP_CARD_ORDER.filter(key => key !== '<combo>').forEach((key) => { on[key] = false })
    // one combo card per option (spec §13.2)
    all.forEach((o) => { on[comboKey(o.lines)] = true })
    Object.assign(on, {
        cover: true,
        providers_network: true,
        step_intro: true,
        step_enroll_plan: true,
        step_actions_keep_plans: canKeepCurrent(ctx),
        recap: true,
        attachments: true,
        disclaimer: true,
        current_plan_costs: (ctx.replacedAll || []).length > 0,
        current_vs_recommended: all.some(o => o.anyNew),
        supp_vs_ma: all.some(o => o.move),
        step_actions_enroll_plan: all.some(o => o.anyNew && o.agentPlans.length > 0),
    })
    return on
}

// Spec §9.2: recompute on/off per key, honour overrides, keep survivors in
// their current (drag) order, insert new keys at their catalog index, drop
// combo keys that are not the current combo, keep unknown non-combo keys.
export function mergeCards (existing, desired, manual = {}, indexOf = () => 0) {
    const on = key => (Object.prototype.hasOwnProperty.call(manual, key) ? !!manual[key] : !!desired[key])
    const known = key => Object.prototype.hasOwnProperty.call(desired, key)
    const cards = (existing || []).filter((key) => {
        if (known(key)) { return on(key) }
        return !/^combo_/.test(key)
    })
    Object.keys(desired).filter(key => on(key) && !cards.includes(key)).forEach((key) => {
        const ki = indexOf(key)
        const at = cards.findIndex(c => indexOf(c) > ki)
        if (at === -1) { cards.push(key) } else { cards.splice(at, 0, key) }
    })
    return cards
}

// ---- context ----------------------------------------------------------------
export function buildContext (input) {
    const { year, payer, coverage, rec, current, plansById, providers = [], providerStatus = () => 'unsure', excludedIds = [], currentNotices = [], option = 1 } = input
    const lns = lines(coverage, rec, current, plansById)
    const sc = scenario(coverage, rec)
    const upgrade = suppUpgrade(coverage, rec, current, plansById)
    const reasons = lns.length ? effectiveReasons(rec, sc.mode, upgrade) : []
    const ctx = {
        year,
        payer: !!payer,
        coverage,
        rec,
        current: current || {},
        plansById: plansById || {},
        lines: lns,
        scenario: sc,
        mode: sc.mode,
        initial: !lns.length,
        move: isMove(lns),
        toSupp: toSupp(coverage, rec),
        toMapd: toMapd(coverage, rec),
        suppApp: suppApp(coverage, rec),
        anyNew: anyNew(lns),
        replaced: replacedPlans(coverage, rec, current, lns),
        resulting: lns.map(l => l.plan),
        upgrade,
        allowedReasons: allowedReasons(sc.mode, upgrade),
        reasons,
        underwriting: rec?.underwriting || defaultUnderwriting(coverage, rec, reasons),
        currentSuppPremium: _.isFinite(parseFloat(rec?.current_supp_premium)) ? parseFloat(rec.current_supp_premium) : null,
        householdDiscount: !!rec?.household_discount,
        note: (rec?.note || '').trim(),
        excludedIds,
        // Dien 2026-09-29 (3, 4): what the resolver knows about the client's
        // current plans for this year: [{ kind, status: 'terminated' |
        // 'consolidated', carrier, name, resolvedName }].
        currentNotices: currentNotices || [],
        providers,
        providerStatus,
        methodOf: plan => method(plan, !!payer),
        // Round-2 spec §13: Option 1 is the primary; Option n an alternative.
        option,
        alternative: option > 1,
        alternatives: [],
    }
    ctx.agentPlans = agentPlans(ctx)
    ctx.selfRxPlans = selfRxPlans(ctx)
    if (option === 1 && lns.length) {
        // Complete, non-duplicate alternatives, numbered in order of survival
        // so the client never sees a gap.
        const seen = new Set([idsKey(ctx.resulting)])
        ;(rec?.alternatives || []).forEach((alt) => {
            const c = buildContext({ ...input, rec: scenarioRec(rec, alt), option: ctx.alternatives.length + 2 })
            const key = c.lines.length ? idsKey(c.resulting) : null
            if (!key || seen.has(key)) { return }
            seen.add(key)
            ctx.alternatives.push(c)
        })
    }
    ctx.replacedAll = _.uniqBy(options(ctx).flatMap(o => o.replaced), 'id')
    return ctx
}

// ---- prose helpers -----------------------------------------------------------
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
export const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ESC[c])
export const bold = s => `<strong>${esc(s)}</strong>`
export const link = (href, text) => `<a href="${esc(href)}" target="_blank" rel="noopener">${esc(text)}</a>`
export const listJoin = a => (a.length <= 1 ? (a[0] || '') : a.length === 2 ? `${a[0]} and ${a[1]}` : `${a.slice(0, -1).join(', ')}, and ${a[a.length - 1]}`)
export const plural = (n, w) => w + (n > 1 ? 's' : '')
export const typeWord = p => (p.type === 'pdp' ? 'drug plan' : p.type === 'supp' ? 'supplement' : 'Medicare Advantage plan')
export const yearlyTotal = p => (parseFloat(p.monthly_premium) || 0) * 12 + (parseFloat(p.yearly_drug_cost) || 0)
const m = v => money(v || 0)
const cap = s => s.charAt(0).toUpperCase() + s.slice(1)
const reasonText = (r, year) => (typeof r.text === 'function' ? r.text(year) : r.text)

// Always the fallback this AEP (spec §8.2 step 3, D10): a year constant's
// presence does not mean CMS published it.
export const PART_B_SENTENCE = year => ` Medicare usually announces the ${year} Part B deductible in mid-November.`

// ---- Plan Recommendation card (A.2, A.3) --------------------------------------
// Dien 2026-09-29 (3): a current plan CMS lists as terminated.
const DO_NOTHING = {
    mapd: 'If you do not choose a new plan, you will be returned to Original Medicare on January 1 and will not have prescription drug coverage.',
    pdp: 'If you do not choose a new plan, you will not have prescription drug coverage starting January 1.',
}
// Its own paragraph above the recommendation (card/AepPlanRecommendation.vue).
// No December 7: a client whose plan ends can enroll through Dec 31 for a
// January 1 start (2026-10-07).
export function terminatedIntroHtml (ctx) {
    return (ctx.currentNotices || []).filter(n => n.status === 'terminated').map((n) => {
        const doNothing = DO_NOTHING[n.kind] ? ` ${DO_NOTHING[n.kind]}` : ''
        return `${esc(n.carrier)} is not offering ${bold(n.name)} in ${ctx.year}, so you will need to choose a new plan for coverage to start January 1.${doNothing}`
    }).join(' ')
}

// Kalena 2026-10-21: a client with a terminated current plan cannot keep
// their coverage, so the letter never offers "keep it instead" or I'm Renewing.
export const canKeepCurrent = ctx => !(ctx?.currentNotices || []).some(n => n.status === 'terminated')

// Dien 2026-09-29 (4): a kept current plan CMS lists as consolidated into another.
export function mergedNotice (ctx, plan) {
    return (ctx.currentNotices || []).find(n => n.status === 'consolidated' && n.kind === plan.type && n.resolvedName) || null
}
export function mergedStayHtml (ctx, n) {
    return `For ${ctx.year}, ${esc(n.carrier)} is combining ${bold(n.name)} into ${bold(n.resolvedName)}. You will be moved to ${bold(n.resolvedName)} automatically on January 1, and after reviewing it, we recommend staying on it. No action is needed.`
}

// Round-2 spec §13.2: the combo card header once the client has options.
export function cardHeader (ctx) {
    const multi = ctx.alternative || (ctx.alternatives || []).length > 0
    if (!multi) { return 'Plan Recommendation:' }
    const names = ctx.resulting.map(p => p.name).join(' + ')
    return ctx.alternative ? `Option ${ctx.option}: ${names}` : `Plan Recommendation (Option 1): ${names}`
}

const NUMBER_WORDS = ['', 'one', 'two', 'three', 'four', 'five']
const moreOptions = n => `${NUMBER_WORDS[n] || n} more ${plural(n, 'option')}`

// An alternative's opening: one sentence, no reasons, no Medicare.gov claim,
// no terminated-plan opening (that belongs to the recommendation).
export function alternativeIntroHtml (ctx) {
    const L = ctx.lines
    if (ctx.toSupp) {
        const supp = L.map(l => l.plan).find(p => p.type === 'supp')
        const pdp = L.map(l => l.plan).find(p => p.type === 'pdp')
        return `As another option, you could move from your Medicare Advantage plan to a Medicare Supplement, ${bold(supp.name)}, paired with a stand-alone drug plan, ${bold(pdp.name)}.`
    }
    if (ctx.toMapd) {
        return `As another option, you could move from your supplement and drug plan to a Medicare Advantage plan, ${bold(L[0].plan.name)}.`
    }
    const changed = L.filter(l => l.act !== 'keep')
    return `As another option, you could switch ${listJoin(changed.map(l => `your ${typeWord(l.plan)} to ${bold(l.plan.name)}`))}.`
}

export function introHtml (ctx) {
    if (ctx.alternative) { return alternativeIntroHtml(ctx) }
    const why = listJoin(REASONS.filter(r => ctx.reasons.includes(r.id)).map(r => esc(reasonText(r, ctx.year))))
    const lead = why ? `Given ${why}, ` : ''
    const L = ctx.lines
    let s
    if (L.every(l => l.act === 'keep')) {
        s = `${lead}we recommend staying on your current ${listJoin(L.map(l => bold(l.plan.name)))} for ${ctx.year}.`
    } else if (ctx.toSupp) {
        const supp = L.map(l => l.plan).find(p => p.type === 'supp')
        const pdp = L.map(l => l.plan).find(p => p.type === 'pdp')
        s = `${lead}we suggest you consider moving from your Medicare Advantage plan to a Medicare Supplement, ${bold(supp.name)}, paired with a stand-alone drug plan, ${bold(pdp.name)}.`
    } else if (ctx.toMapd) {
        s = `${lead}we suggest you consider moving from your supplement and drug plan to a Medicare Advantage plan, ${bold(L[0].plan.name)}.`
    } else {
        s = `${lead}we suggest ${listJoin(L.map(l => (l.act === 'keep' ? `keeping your current ${bold(l.plan.name)}` : `switching your ${typeWord(l.plan)} to ${bold(l.plan.name)}`)))}.`
    }
    s = cap(s)
    const rx = L.map(l => l.plan).find(p => p.type === 'pdp')
    if (ctx.reasons.includes('rx') && rx) {
        s += ` According to ${link(MEDICARE_URL, 'Medicare.gov')}, ${bold(rx.name)} is the most cost-effective drug plan based on your prescriptions.`
    }
    const n = (ctx.alternatives || []).length
    if (n) { s += ` We also included ${moreOptions(n)} below for you to compare.` }
    return s
}

// The PDP/MAPD total-cost sentence: switch lines with a matched current plan only.
// "in {year}", not "per year": the saving is this plan year's estimate and
// changes year to year (2026-10-06).
export function comparisonHtml (line, year) {
    const p = line.plan
    const f = line.from
    if (!f || p.type === 'supp') { return '' }
    const a = yearlyTotal(p)
    const b = yearlyTotal(f)
    const d = Math.abs(a - b)
    if (d < 1) { return '' }
    const tail = a < b ? `a savings of approximately <strong>${m(d)} in ${year}</strong>` : `approximately ${m(d)} more in ${year}`
    return ` Including premiums and estimated prescription costs, your total yearly cost would be approximately <strong>${m(a)}</strong>, compared to ${m(b)} on your current plan, ${tail}.`
}

// Dien 2026-09-29 (1, 1a): a high-deductible G or F pays nothing until its own
// yearly deductible is met; the Part B deductible counts toward it.
export const HD_BODY = "you pay your share of Medicare-covered costs, including the Part B deductible, until you reach the plan's yearly deductible. After that, the plan covers your share of Medicare-approved costs for the rest of the year."
const isHD = p => /^HD[FG]$/.test(planLetter(p) || '')
const SUPP_COVER = {
    G: 'Plan G pays your share of Medicare-approved costs after the annual Part B deductible, so you have very little out of pocket for doctor visits, hospital stays and outpatient care.',
    // Kalena 2026-09-30: the copays spelled out.
    N: "With Plan N, once you've met the annual Part B deductible, you'll pay a $20 copay for doctor visits and $50 for emergency room visits.",
}

// Medigap letters that pay the Part B deductible themselves (Plan C, Plan F,
// Massachusetts Supplement 1), so "you pay the Part B deductible" and the
// mid-November announcement do not apply (Aziz 2026-10-05). High-deductible
// F is 'HDF' to planLetter and keeps the high-deductible copy.
const COVERS_PART_B_DEDUCTIBLE = ['C', 'F', '1']
export const coversPartBDeductible = p => COVERS_PART_B_DEDUCTIBLE.includes(planLetter(p))

// What a drug deductible applies to, worded as the non-AEP summary words it
// (PlanLegacy): "a $700 annual deductible" alone reads as covering every
// prescription even when the copay table says tier 1 drugs skip it. Unknown
// tiers or no deductible keep the definition. The store resolves the tiers
// (util/deductible-tiers.js, #731): the plan's CMS values, an agent's
// override, or the client's own drugs.
export function rxDeductibleScope (deductible, tiers, all = false) {
    const text = deductibleTiersText(tiers, all)
    if (!deductible || !text) { return '(this is the amount you have to pay out of pocket each year before the plan begins to pay)' }
    return `that applies to ${text}`
}

// The non-AEP MAPD paragraph's pointer (PlanLegacy), for a Medicare Advantage
// plan whose document the Attachments card links (store planBenefitsDocLinked;
// 2026-10-21). Without one there is nothing at the end to find.
export const BENEFITS_DOC_HTML = '<p>Please also find the link to view the detailed benefits document at the end of this email.</p>'

export function planDetailHtml (ctx, line) {
    const p = line.plan
    const y = ctx.year
    if (p.type === 'pdp') {
        return `<p>For ${y}, ${bold(p.name)} has a<strong> ${m(p.monthly_premium)}/month </strong>premium and a<strong> ${m(p.drug_deductible)} </strong>annual deductible ${rxDeductibleScope(p.drug_deductible, p.drug_deductible_applicable_tiers, p.drug_deductible_all_tiers)}.${comparisonHtml(line, y)}</p>`
    }
    if (p.type === 'mapd') {
        return `<p>For ${y}, ${bold(p.name)} has a<strong> ${m(p.monthly_premium)}/month </strong>premium,<strong> ${m(p.medical_deductible)} </strong>medical deductible,<strong> ${m(p.drug_deductible)} </strong>prescription deductible and a<strong> ${m(p.moop)} </strong>annual in-network max out-of-pocket (this is the maximum amount you'll have to pay in a calendar year for covered medical expenses. Once you reach this limit, your plan will cover 100% of eligible costs for the remainder of the year).${comparisonHtml(line, y)}</p>${p.benefits_doc ? BENEFITS_DOC_HTML : ''}`
    }
    const coversB = coversPartBDeductible(p)
    const partB = coversB ? '' : PART_B_SENTENCE(y)
    if (line.act === 'keep' && coversB) {
        return `<p>Your ${bold(p.name)} supplement stays the same for ${y}, and you can continue to see any doctor in the country who accepts Medicare.</p>`
    }
    if (line.act === 'keep') {
        // a kept Plan N gets its copay sentence instead of the generic
        // reminder (2026-10-06), the same one a new Plan N gets below
        const reminder = isHD(p)
            ? ` As a reminder, with a high-deductible plan, ${HD_BODY}`
            : planLetter(p) === 'N' ? ` ${SUPP_COVER.N}` : ' As a reminder, you pay the Part B deductible each year before your supplement begins to pay.'
        return `<p>Your ${bold(p.name)} supplement stays the same for ${y}, and you can continue to see any doctor in the country who accepts Medicare.${reminder}${partB}</p>`
    }
    // Plan G, Plan N and the high-deductible letters have copy; any other
    // letter gets no coverage sentence rather than a wrong one.
    const cover = isHD(p) ? ` With a high-deductible plan, ${HD_BODY}` : SUPP_COVER[planLetter(p)] ? ` ${SUPP_COVER[planLetter(p)]}` : ''
    let sc = ''
    if (line.act === 'switch') {
        const c = ctx.currentSuppPremium
        const prem = parseFloat(p.monthly_premium) || 0
        if (c !== null && c !== prem) {
            const d = Math.abs(c - prem)
            sc = ` Based on the ${m(c)}/month you shared with us for your current supplement, ${prem < c ? `this would save you about <strong>${m(d)}/month</strong> (${m(d * 12)} per year).` : `this would cost about ${m(d)}/month more (${m(d * 12)} per year).`}`
        } else if (c === null) {
            sc = ' You can compare this with the premium on your current supplement bill.'
        }
    }
    const hh = ctx.householdDiscount ? ', which includes a household discount' : ''
    return `<p>For ${y}, ${bold(p.name)} has a<strong> ${m(p.monthly_premium)}/month </strong>premium${hh}.${cover} You can continue to see any doctor in the country who accepts Medicare.${partB}${sc}</p>`
}

export function noteHtml (note) {
    const t = (note || '').trim()
    return t ? `<p class="advisor-note">${esc(t).replace(/\n/g, '<br>')}</p>` : ''
}

// ---- Current Plan Costs (A.10) ------------------------------------------------
export const currentCostsTitle = ctx => `Current Plan Costs: ${(ctx.replacedAll || ctx.replaced).map(p => p.name).join(' + ')}`
export const currentCostsIntro = ctx => `For comparison, here is what your current coverage looks like for ${ctx.year}:`

export function currentSummaryHtml (ctx, p) {
    if (p.type === 'pdp') {
        return `<p>Your current ${bold(p.name)} has a<strong> ${m(p.monthly_premium)}/month </strong>premium and a<strong> ${m(p.drug_deductible)} </strong>annual deductible ${rxDeductibleScope(p.drug_deductible, p.drug_deductible_applicable_tiers, p.drug_deductible_all_tiers)}.</p>`
    }
    if (p.type === 'mapd') {
        return `<p>Your current ${bold(p.name)} has a<strong> ${m(p.monthly_premium)}/month </strong>premium,<strong> ${m(p.medical_deductible)} </strong>medical deductible,<strong> ${m(p.drug_deductible)} </strong>prescription deductible and a<strong> ${m(p.moop)} </strong>annual in-network max out-of-pocket.</p>`
    }
    const c = ctx.currentSuppPremium
    if (isHD(p)) {
        const head = c !== null
            ? `Your current ${bold(p.name)} has a<strong> ${m(c)}/month </strong>premium (as you shared with us). It is a high-deductible supplement:`
            : `Your current ${bold(p.name)} is a high-deductible supplement:`
        return `<p>${head} ${HD_BODY} You can see any doctor who accepts Medicare.</p>`
    }
    const head = c !== null
        ? `Your current ${bold(p.name)} has a<strong> ${m(c)}/month </strong>premium (as you shared with us). It pays`
        : `Your current ${bold(p.name)} supplement pays`
    const partB = coversPartBDeductible(p) ? ', including the Part B deductible,' : ' after the annual Part B deductible,'
    return `<p>${head} your share of Medicare-approved costs${partB} and you can see any doctor who accepts Medicare.</p>`
}

export function rxIntroHtml (plan) {
    const mail = !!plan.pharmacy_mail_order
    const where = plan.pharmacy_name ? `${mail ? 'via' : 'at'} <strong>${esc(plan.pharmacy_name)}</strong>` : 'via mail order pharmacy'
    // The wording follows the table's columns (util/drug-table.js): quarters
    // when every mail-order fill is 90-day, months when one drug stays a
    // 30-day fill (a tier with no 90-day supply, Dien 2026-10-02), and then
    // the 90-day ones land every third month.
    const quarterly = plan.pharmacy_period === 'quarter'
    const every3 = mail && !quarterly && plan.pharmacy_name ? ' Drugs filled 90 days at a time show a cost every third month.' : ''
    return `The table below shows your ${quarterly ? 'quarterly' : 'monthly'} copay estimates ${where} (data sourced from ${link(MEDICARE_URL, 'Medicare.gov')}).${every3}`
}

// ---- Recap (A.13) --------------------------------------------------------------
export function recapHtml (payer) {
    const lead = 'I know this is a lot of information so please feel free to reach out anytime if you have any questions! '
    return payer
        ? `<p>${lead}Our office number is <a href="${OFFICE_PHONE_HREF}">${OFFICE_PHONE}</a> and you can also reply directly to this email with any questions.</p>`
        : `<p>${lead}You can email us at <a href="mailto:${HELP_EMAIL}">${HELP_EMAIL}</a> or reply directly to this email.</p>`
}

// ---- Next steps (A.4–A.9) --------------------------------------------------------
export function enrollLeadHtml (ctx, plan) {
    if (ctx.methodOf(plan) === 'agent') {
        return `To enroll in ${bold(plan.name)}, simply select the "Start Enrollment" button below or reply to this email, and our team will set up the electronic paperwork and send it to you through PandaDoc, our secure e-signature software. We will email you once it has been approved.`
    }
    if (plan.type === 'supp') {
        const phone = ctx.payer ? ' We are happy to walk you through the application over the phone.' : ''
        return `${bold(plan.name)} is applied for directly with ${esc(plan.carrier)}. You can apply on ${esc(plan.carrier)}'s website or by calling their enrollment line.${phone}`
    }
    return `We are not able to submit the enrollment for ${bold(plan.name)} for you, so you will enroll online through Medicare.gov. It only takes a few minutes, and the steps are below.`
}

export const deadlineHtml = (ctx, plan) => ` Please ${ctx.methodOf(plan) === 'self' ? 'complete your enrollment' : 'let us know'} by <strong>December 7</strong>, the last day of the Annual Enrollment Period.`

export const underwritingHtml = ctx => (ctx.underwriting === 'no'
    ? 'Based on your situation, this application should not require health questions. The carrier will confirm this when they review your application.'
    : 'Supplement applications usually include health questions, and the carrier must approve your application before coverage starts.')

// Dien 2026-10-03: BCBSRI is the only carrier not mailing new Medicare
// Advantage cards for 2027. A one-AEP rule; revisit before the next one.
export const isBcbsriMapd = plan => plan?.type === 'mapd' && /blue cross.*rhode island/i.test(plan.carrier || '')

export const cardNote = (plan, year) => (plan.type === 'supp'
    ? 'You can continue to use your existing supplement card.'
    : plan.type === 'pdp'
        ? 'You will receive a new card for your prescription (PDP) plan by mid-December.'
        : isBcbsriMapd(plan)
            ? `BCBSRI will not send out a new card for ${year} so you will want to keep your existing Medicare Advantage plan card.`
            : 'You will receive a new card for your Medicare Advantage plan by mid-December.')

export function medicareGovStepsHtml (ctx, plan) {
    const type = plan.type === 'pdp' ? 'Medicare drug plan (Part D)' : 'Medicare Advantage Plan (Part C)'
    const help = ctx.payer
        ? `<p>If you would like help, we are happy to walk you through these steps over the phone. Just call us at ${OFFICE_PHONE} or reply to this email to set up a time.</p>`
        : `<p>If you get stuck, call Medicare at ${MEDICARE_PHONE} and a representative can walk you through the online enrollment. You can also email us at ${HELP_EMAIL} with any questions.</p>`
    return `<div class="mgov"><h5 class="text-primary">How to Enroll in ${esc(plan.name)}</h5><ol>` +
        `<li>Go to ${link(MEDICARE_URL, 'www.medicare.gov')}. Click <strong>Find health &amp; drug plans</strong>, then <strong>Continue without logging in</strong>.</li>` +
        `<li>Choose <strong>${type}</strong>. Make sure the plan year says ${ctx.year}.</li>` +
        '<li>Answer the question about Medicaid or Extra Help, then click <strong>Next</strong>.</li>' +
        '<li>Confirm your medications and pharmacy, or skip this step.</li>' +
        `<li>Find ${bold(plan.name)} in the list.</li>` +
        '<li>Click <strong>Plan Details</strong>, then the <strong>Enroll</strong> button.</li>' +
        '<li>Enter your Medicare number and your Part A and Part B start dates (you can find these on your Medicare card).</li>' +
        '<li>Type your name to sign, then click <strong>Submit</strong>.</li></ol>' +
        '<p class="mb-1">Save the confirmation number on the final screen. That is your proof of enrollment.</p>' +
        `${help}</div>`
}

// Dien 2026-11-04: the regular (non-AEP) summary's Enroll In Additional
// Coverage card tells the client how to enroll in a plan we cannot submit, the
// way the AEP card does: the carrier sentence for a supplement, the
// Medicare.gov sentence and steps for a drug or Medicare Advantage plan. No
// start dates or December 7 deadline: those are the AEP's. Engine-shape plans
// (toEnginePlan); the caller decides which plans are self-enroll.
export function selfEnrollItems ({ payer, year }, plans) {
    const ctx = { payer, year, methodOf: () => 'self' }
    return plans.map(p => ({ plan: p, lead: enrollLeadHtml(ctx, p), steps: p.type === 'supp' ? null : medicareGovStepsHtml(ctx, p) }))
}

export function stayInsteadHtml (ctx) {
    let what
    if (ctx.toSupp) {
        what = 'your current Medicare Advantage plan'
    } else if (ctx.toMapd) {
        what = 'your current supplement and drug plan'
    } else {
        what = listJoin(ctx.lines.filter(l => l.act === 'switch').map(l => (l.from ? `your current ${bold(l.from.name)}` : `your current ${typeWord(l.plan)}`)))
    }
    return `If you would prefer to keep ${what} instead, please select the "I'm Renewing" button below or reply to this email to let our team know.`
}

export function enrollHeader (ctx) {
    const changed = ctx.lines.filter(l => l.act !== 'keep').length
    const kept = ctx.lines.length - changed
    return changed ? plural(changed, 'Enroll In Recommended Plan') : plural(kept, 'Stay On Current Plan')
}

const item = (html, plan = null, carrierComponent = null) => ({ html, carrierComponent, plan })

export function enrollSteps (ctx, { multi = false } = {}) {
    const L = ctx.lines
    const y = ctx.year
    const changed = L.filter(l => l.act !== 'keep')
    const kept = L.filter(l => l.act === 'keep')
    if (!changed.length) {
        const merged = kept.filter(l => mergedNotice(ctx, l.plan))
        const rest = kept.filter(l => !mergedNotice(ctx, l.plan))
        const items = merged.map(l => item(mergedStayHtml(ctx, mergedNotice(ctx, l.plan)), l.plan))
        if (rest.length) {
            items.push(item(`Your ${plural(rest.length, 'plan')} will automatically renew for ${y}. No action is required, but you can confirm your renewal with our team by selecting "I'm Renewing" below or by replying to this email.`))
        }
        items.push(item(kept.map(l => cardNote(l.plan, y)).join(' ')))
        return { kind: 'stay', intro: null, items, outro: [], after: [] }
    }
    if (ctx.toSupp) {
        const sp = L.map(l => l.plan).find(p => p.type === 'supp')
        const pp = L.map(l => l.plan).find(p => p.type === 'pdp')
        const ppSelf = ctx.methodOf(pp) === 'self'
        // Dien 2026-09-29 (2): in the sequence the drug plan always goes
        // through Medicare.gov, carrier component or not.
        const step3 = !ppSelf
            ? `we will enroll you in ${bold(pp.name)}.`
            : `enroll in ${bold(pp.name)} through Medicare.gov using the steps below.`
        const after = !ppSelf
            ? []
            : [{ kind: 'mgov', html: medicareGovStepsHtml(ctx, pp), carrierComponent: null, plan: pp }]
        return {
            kind: 'sequence',
            intro: 'Because this change includes a supplement application, we complete it in this order so you are never without coverage:',
            items: [
                item(ctx.methodOf(sp) === 'agent'
                    ? `Select the "Start Enrollment" button below or reply to this email, and our team will send you the ${bold(sp.name)} application through PandaDoc, our secure e-signature software, with a 1/1/${y} start date.`
                    : `${enrollLeadHtml(ctx, sp)} Ask for a 1/1/${y} start date.`, sp),
                item(`${underwritingHtml(ctx)}${ctx.underwriting === 'no' ? '' : ' This typically takes 1-2 weeks.'}`),
                item(`Once your supplement is approved, ${step3} Enrolling in a stand-alone drug plan automatically ends your Medicare Advantage plan on 12/31.`, pp),
                item(`Your supplement and drug plan both start on 1/1/${y}. Keep using your current Medicare Advantage card until then.`),
            ],
            // Round-2 spec §13.2: with several options the closing sentence is
            // shared, printed once after every block.
            // Dien 2026-10-21: an ending plan ends 12/31 whatever happens to
            // the supplement, and the client can enroll through December 31.
            outro: [
                canKeepCurrent(ctx)
                    ? 'If the supplement is not approved, nothing changes: you stay on your current plan and we will review other options with you. Please let us know as early as you can, since both steps need to be finished before the Annual Enrollment Period ends on <strong>December 7</strong>.'
                    : 'If the supplement is not approved, your current plan still ends on December 31, so we will help you choose another plan to start January 1. Please let us know as early as you can so there is time to finish both steps.',
            ].concat(multi || !canKeepCurrent(ctx) ? [] : [stayInsteadHtml(ctx)]),
            after,
        }
    }
    const items = changed.map((l) => {
        const p = l.plan
        const lead = `${enrollLeadHtml(ctx, p)} `
        let tail
        if (p.type === 'supp') {
            tail = `${underwritingHtml(ctx)} Please keep your current supplement active until your new plan is approved. Once it is approved, please call ${l.from ? esc(l.from.carrier) : 'your current carrier'} to cancel your current policy so you do not pay for both.`
        } else if (p.type === 'pdp') {
            tail = `Your new drug plan will start on 1/1/${y} and will automatically replace your current drug plan.${deadlineHtml(ctx, p)}`
        } else if (ctx.toMapd) {
            const carrier = ctx.current.supp ? esc(ctx.current.supp.carrier) : 'your supplement carrier'
            tail = `Your new Medicare Advantage plan will start on 1/1/${y} and will automatically end your current drug plan. Your supplement does not cancel on its own, so once your enrollment is confirmed, please call ${carrier} to cancel your supplement effective 12/31. If you want to return to a supplement later, you may have to answer health questions.${deadlineHtml(ctx, p)}`
        } else {
            tail = `Your new plan will start on 1/1/${y} and will automatically replace your current Medicare Advantage plan.${deadlineHtml(ctx, p)}`
        }
        return item(`${lead}${tail}`, p)
    })
    kept.forEach((l) => {
        const n = mergedNotice(ctx, l.plan)
        items.push(item(n ? `${mergedStayHtml(ctx, n)} ${cardNote(l.plan, y)}` : `Your ${bold(l.plan.name)} will automatically renew for ${y}. ${cardNote(l.plan, y)}`, l.plan))
    })
    if (!multi && canKeepCurrent(ctx)) { items.push(item(stayInsteadHtml(ctx))) }
    const after = ctx.selfRxPlans.map(p => ({ kind: 'mgov', html: medicareGovStepsHtml(ctx, p), carrierComponent: null, plan: p }))
    return { kind: 'bullets', intro: null, items, outro: [], after }
}

// Round-2 spec §13.2: the next-steps card, one block per option.
export const STAY_CLOSING = 'If you would prefer to keep your current coverage instead, please select the "I\'m Renewing" button below or reply to this email to let our team know.'
export function enrollBlocks (ctx) {
    const all = options(ctx)
    if (all.length === 1) {
        return { multi: false, header: enrollHeader(ctx), intro: null, blocks: [{ option: 1, title: null, steps: enrollSteps(ctx) }], closing: null }
    }
    return {
        multi: true,
        header: 'Enroll In The Option You Choose',
        intro: 'Let us know which option you would like, and here is what happens next for each:',
        blocks: all.map(o => ({ option: o.option, title: `Option ${o.option}: ${o.resulting.map(p => p.name).join(' + ')}`, steps: enrollSteps(o, { multi: true }) })),
        // a staying option already says how to renew
        closing: all.some(o => !o.anyNew) || !canKeepCurrent(ctx) ? null : STAY_CLOSING,
    }
}

// Plans Actions Enroll (A.9). The card shows one block per option (round-2
// spec §13.2, Aziz 2026-09-30): agentPlansAll gates the card, the notes are
// computed per option so Option 1's notes never sit under Option 2's button.
export const agentPlansAll = ctx => _.uniqBy(options(ctx).flatMap(o => o.agentPlans), 'id')
export function actionsNotes (ctx) {
    const pdp = ctx.toSupp ? ctx.lines.map(l => l.plan).find(p => p.type === 'pdp') : null
    return {
        holdPdp: !!(pdp && ctx.methodOf(pdp) === 'agent'),
        selfNames: ctx.lines.filter(l => l.act !== 'keep' && ctx.methodOf(l.plan) === 'self').map(l => l.plan.name),
    }
}

// ---- Current vs. Recommended (A.11) ---------------------------------------------
const NOT_CONFIRMED = '<span class="text-muted">Not confirmed</span>'
const NEEDS_PREMIUM = '<span class="text-muted">Needs current premium</span>'

export function compareRows (ctx) {
    const all = options(ctx)
    if (!all.some(o => o.anyNew)) { return null }
    const multi = all.length > 1
    let curPlans
    let columns
    let currentLabel = 'Current'
    if (!multi) {
        curPlans = ctx.replaced
        // Both columns cover the same lines: a switch whose current plan is
        // unmatched has nothing to compare against (review I2).
        const recPlans = ctx.move ? ctx.resulting : ctx.lines.filter(l => l.act === 'switch' && l.from).map(l => l.plan)
        if (!curPlans.length || !recPlans.length) { return null }
        columns = [{ label: 'Recommended', plans: recPlans }]
    } else {
        // Round-2 spec §13.2: the union of the lines any option changes (a move
        // changes every line of the coverage), limited to lines with a matched
        // current plan; each option column holds its plans of those lines.
        const changed = new Set()
        all.forEach((o) => {
            if (o.move) { (KINDS_BY_COVERAGE[ctx.coverage] || []).forEach(k => changed.add(k)) } else { o.lines.filter(l => l.act === 'switch').forEach(l => changed.add(l.plan.type)) }
        })
        const kinds = (KINDS_BY_COVERAGE[ctx.coverage] || []).filter(k => changed.has(k) && ctx.current[k])
        curPlans = kinds.map(k => ctx.current[k])
        columns = all.map(o => ({ label: `Option ${o.option}`, plans: o.move ? o.resulting : o.lines.filter(l => kinds.includes(l.plan.type)).map(l => l.plan) }))
        if (!curPlans.length || columns.some(c => !c.plans.length)) { return null }
        // An option that keeps the current plans IS the Current column; a
        // column of its own repeated it (Kalena 2026-10-13, client 10072).
        const staying = columns.filter(c => idsKey(c.plans) === idsKey(curPlans))
        if (staying.length) {
            currentLabel = `Current (${staying.map(c => c.label).join(', ')})`
            columns = columns.filter(c => !staying.includes(c))
        }
    }
    const isCurrentSupp = p => p.type === 'supp' && curPlans.includes(p)
    const medOf = ps => ps.find(p => p.type !== 'pdp')
    const rxOf = ps => ps.find(p => p.type !== 'supp')
    const sets = [curPlans].concat(columns.map(c => c.plans))
    const hasMed = sets.some(ps => !!medOf(ps))
    const hasRx = sets.some(ps => !!rxOf(ps))
    const premOf = (ps) => {
        let t = 0
        for (const p of ps) {
            const v = isCurrentSupp(p) ? ctx.currentSuppPremium : (parseFloat(p.monthly_premium) || 0)
            if (v === null) { return null }
            t += v
        }
        return t
    }
    const cell = (ps, k) => {
        const md = medOf(ps)
        const rx = rxOf(ps)
        switch (k) {
        case 'prem': { const v = premOf(ps); return v === null ? NOT_CONFIRMED : m(v) }
        case 'docs': return !md ? '' : md.type === 'supp' ? 'Any doctor who accepts Medicare' : `Plan network${md.mapd_type ? ` (${esc(md.mapd_type)})` : ''}`
        case 'med': {
            if (!md) { return '' }
            if (md.type !== 'supp') { return `Copays, ${m(md.moop)} in-network yearly max` }
            // Only the two letters Appendix A wrote copy for; any other
            // supplement gets no coverage claim (review C1).
            const letter = planLetter(md)
            return letter === 'G' ? 'Plan G covers your share after the Part B deductible' : letter === 'N' ? 'Plan N: small copays after the Part B deductible' : ''
        }
        case 'dvh': return !md ? '' : md.type === 'supp' ? 'Not included' : 'Included (limits vary by plan)'
        case 'rxded': return rx ? m(rx.drug_deductible) : ''
        case 'drug': return rx ? m(rx.yearly_drug_cost) : ''
        case 'tot': { const v = premOf(ps); return v === null ? NEEDS_PREMIUM : `<strong>${m(v * 12 + (rx ? (parseFloat(rx.yearly_drug_cost) || 0) : 0))}</strong>` }
        default: return ''
        }
    }
    const spec = [['Monthly premium', 'prem']]
        .concat(hasMed ? [['Doctors', 'docs'], ['Medical costs', 'med'], ['Dental, vision and hearing', 'dvh']] : [])
        .concat(hasRx ? [['Drug deductible', 'rxded'], ['Estimated yearly drug cost', 'drug'], ['Estimated total yearly cost (premiums + drugs)', 'tot']] : [['Yearly premium', 'tot']])
    return {
        currentLabel,
        current: curPlans.map(p => p.name).join(' + '),
        columns: columns.map(c => ({ label: c.label, names: c.plans.map(p => p.name).join(' + ') })),
        rows: spec.map(([label, k]) => ({ label, current: cell(curPlans, k), values: columns.map(c => cell(c.plans, k)) })),
        drugFootnote: hasRx,
    }
}

// A MAPD -> MAPD switch can use the non-AEP Compare Plans chart
// (card/GroupPlansCompare.vue, 2026-10-07): the client's current MAPD first,
// then each option's new MAPD, once each, so the copays sit side by side.
// [] unless the current MAPD is matched and an option switches it.
export function mapdCompareColumns (ctx) {
    if (!ctx || ctx.coverage !== 'MAPD' || !ctx.current.mapd) { return [] }
    const news = options(ctx).flatMap(o => o.lines.filter(l => l.act === 'switch' && l.plan.type === 'mapd').map(l => l.plan))
    return news.length ? _.uniqBy([ctx.current.mapd, ...news], 'id') : []
}

// ---- Supplement vs. Medicare Advantage (A.12) ------------------------------------
export function suppVsMaRows () {
    return {
        columns: ['Medicare Supplement + drug plan', 'Medicare Advantage'],
        rows: [
            { label: 'Doctors and hospitals', supp: 'Any doctor or hospital in the U.S. that accepts Medicare. No referrals needed.', mapd: "Doctors and hospitals in the plan's network. HMOs usually require referrals to see specialists." },
            { label: 'Monthly cost', supp: 'Higher monthly premiums (supplement plus drug plan).', mapd: 'Often a low or $0 monthly premium.' },
            { label: 'Costs when you get care', supp: 'Low and predictable. With Plan G, you pay only the Part B deductible each year.', mapd: "Copays and coinsurance for most services, up to the plan's yearly out-of-pocket maximum." },
            { label: 'Dental, vision and hearing', supp: 'Not included.', mapd: 'Often included, with limits that vary by plan.' },
            { label: 'Travel', supp: 'Works anywhere in the U.S.', mapd: "Mostly limited to the plan's network, except for emergencies and urgent care. PPOs may cover out-of-network care at a higher cost." },
            { label: 'Changing later', supp: 'You can change your drug plan every Annual Enrollment Period. Changing supplements may require health questions.', mapd: 'You can change plans every Annual Enrollment Period. Moving to a supplement later may require health questions.' },
        ],
    }
}

// ---- Legacy summaries (spec §11) -------------------------------------------------
// A proposal for a summary that has a mode but no recommendation. Never
// verified: the advisor confirms current coverage first.
export function deriveFromLegacy (mode, comboPlans, guess, surveyCoverage) {
    const g = guess || {}
    const gid = k => (g[k] && g[k].id) || null
    const ids = { supp: null, pdp: null, mapd: null }
    ;(comboPlans || []).forEach((p) => {
        if (p.type === 'supp') { ids.supp = p.id } else if (p.type === 'pdp') { ids.pdp = p.id } else { ids.mapd = p.id }
    })
    let coverage = COVERAGES.includes(surveyCoverage) ? surveyCoverage : null
    if (!coverage && mode) {
        coverage = /SUPP/.test(mode) ? 'SUPP_PDP' : /MAPD/.test(mode) ? 'MAPD' : 'PDP'
    }
    const rec = { ...DEFAULT_RECOMMENDATION, coverage, current: { supp_id: gid('supp'), pdp_id: gid('pdp'), mapd_id: gid('mapd'), verified: false } }
    const suppPdp = coverage === 'SUPP_PDP'
    switch (mode) {
    case 'STAY_PDP':
        return { ...rec, drug: 'keep', medical: suppPdp ? 'keep' : null, current: { ...rec.current, pdp_id: ids.pdp || rec.current.pdp_id } }
    case 'SWITCH_PDP':
        return { ...rec, drug: 'switch', medical: suppPdp ? 'keep' : null, pdp_id: ids.pdp }
    case 'STAY_MAPD':
        return { ...rec, medical: 'keep', current: { ...rec.current, mapd_id: ids.mapd || rec.current.mapd_id } }
    case 'SWITCH_MAPD':
        return { ...rec, medical: 'switch', mapd_id: ids.mapd }
    case 'STAY_SUPP_PDP':
        return { ...rec, medical: 'keep', drug: 'keep', current: { ...rec.current, supp_id: ids.supp || rec.current.supp_id, pdp_id: ids.pdp || rec.current.pdp_id } }
    case 'SWITCH_SUPP':
        return { ...rec, medical: 'switch', drug: 'keep', supp_id: ids.supp, current: { ...rec.current, pdp_id: ids.pdp || rec.current.pdp_id } }
    case 'SWITCH_SUPP_PDP':
        return { ...rec, medical: 'switch', drug: 'switch', supp_id: ids.supp, pdp_id: ids.pdp }
    default:
        return rec
    }
}
